import { ref, get } from 'firebase/database';
import { db as database } from './firebaseConfig';

/**
 * Course Data Service
 * Handles all Firebase queries for landing pages
 * Separates LMS data (/testmeta) from marketing data (/marketing_meta)
 */

// ============ MARKETING DATA ============

/**
 * Fetch all marketing metadata for a course
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} Marketing data (hero, outcomes, features, pricing, etc.)
 */
export const fetchMarketingMetadata = async (courseId) => {
  try {
    const marketingRef = ref(database, `/marketing_meta/courses/${courseId}`);
    const snapshot = await get(marketingRef);

    if (!snapshot.exists()) {
      throw new Error('Course information not found'); //, <a href="/course/11"> Fullstack Course </a>
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error fetching marketing metadata:', error);
    throw error;
  }
};

/**
 * Fetch specific section from marketing data
 * @param {string} courseId - Course ID
 * @param {string} section - Section name (hero, outcomes, features, etc.)
 * @returns {Promise<Object>} Section data or null if missing
 */
export const fetchMarketingSection = async (courseId, section) => {
  try {
    const sectionRef = ref(database, `/marketing_meta/courses/${courseId}/${section}`);
    const snapshot = await get(sectionRef);

    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error(`Error fetching ${section} section:`, error);
    return null;
  }
};

// ============ LMS DATA ============

/**
 * Fetch LMS course metadata
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} Course metadata (name, description, slug, status)
 */
export const fetchLMSCourseMetadata = async (courseId) => {
  try {
    const courseRef = ref(database, `/testmeta/courses/${courseId}`);
    const snapshot = await get(courseRef);

    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error(`Error fetching LMS course metadata for ${courseId}:`, error);
    return null;
  }
};

// Cache for the complete hierarchy to avoid multiple fetches
let hierarchyCache = null;

/**
 * Fetch complete hierarchy once and cache it
 * @returns {Promise<Object>} Complete hierarchy structure from /testmeta/hierarchy/
 */
const fetchCompleteHierarchy = async () => {
  if (hierarchyCache) {
    console.log('[courseService] Using cached hierarchy');
    return hierarchyCache;
  }

  try {
    const hierarchyRef = ref(database, `/testmeta/hierarchy/`);
    console.log('[courseService] Fetching complete hierarchy from /testmeta/hierarchy/');
    const snapshot = await get(hierarchyRef);

    if (!snapshot.exists()) {
      throw new Error('Hierarchy not found at /testmeta/hierarchy/');
    }

    hierarchyCache = snapshot.val();
    console.log('[courseService] Complete hierarchy cached:', hierarchyCache);
    return hierarchyCache;
  } catch (error) {
    console.error('[courseService] Error fetching hierarchy:', error);
    throw error;
  }
};

/**
 * Fetch subject hierarchy (lessons for a specific subject)
 * @param {number} subjectId - Subject ID
 * @returns {Promise<Array>} Array of lesson IDs for the subject
 */
export const fetchSubjectHierarchy = async (subjectId) => {
  try {
    const hierarchyRef = ref(database, `/testmeta/hierarchy/${subjectId}`);
    const snapshot = await get(hierarchyRef);

    const lessonData = snapshot.val();

    // Extract lesson IDs from object keys (object structure: {lessonId: {testId: ...}})
    const lessonIds = lessonData ? Object.keys(lessonData).map(Number) : [];

    //console.log(`[courseService] Subject ${subjectId} lessons:`, lessonIds);

    return lessonIds;
  } catch (error) {
    console.error(`Error fetching hierarchy for subject ${subjectId}:`, error);
    return [];
  }
};

/**
 * Fetch curriculum hierarchy structure
 * @param {string} hierarchyId - Hierarchy ID from curriculum.hierarchyId (DEPRECATED - use fetchOrganizationHierarchy)
 * @returns {Promise<Object>} Hierarchy structure with subject/lesson IDs
 */
export const fetchCurriculumHierarchy = async (hierarchyId) => {
  try {
    const hierarchyRef = ref(database, `/testmeta/hierarchy/${hierarchyId}`);
    const snapshot = await get(hierarchyRef);

    if (!snapshot.exists()) {
      throw new Error('Curriculum hierarchy not found');
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error fetching curriculum hierarchy:', error);
    throw error;
  }
};

/**
 * Fetch subject metadata
 * @param {string} subjectId - Subject ID
 * @returns {Promise<Object>} Subject data (id, title, description)
 */
export const fetchSubject = async (subjectId) => {
  try {
    const subjectRef = ref(database, `/testmeta/subjects/${subjectId}`);
    const snapshot = await get(subjectRef);

    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error(`Error fetching subject ${subjectId}:`, error);
    return null;
  }
};

/**
 * Fetch lesson metadata
 * @param {string} lessonId - Lesson ID
 * @returns {Promise<Object>} Lesson data (id, title, description)
 */
export const fetchLesson = async (lessonId) => {
  try {
    const lessonRef = ref(database, `/testmeta/lessons/${lessonId}`);
    const snapshot = await get(lessonRef);

    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error(`Error fetching lesson ${lessonId}:`, error);
    return null;
  }
};

/**
 * Fetch all subjects (bulk fetch)
 * @param {string[]} subjectIds - Array of subject IDs
 * @returns {Promise<Object>} Map of subjectId -> subject data
 */
export const fetchMultipleSubjects = async (subjectIds) => {
  try {
    const subjectsPromises = subjectIds.map(id => fetchSubject(id));
    const subjectsArray = await Promise.all(subjectsPromises);

    const subjectsMap = {};
    subjectIds.forEach((id, index) => {
      if (subjectsArray[index]) {
        subjectsMap[id] = subjectsArray[index];
      }
    });

    return subjectsMap;
  } catch (error) {
    console.error('Error fetching multiple subjects:', error);
    return {};
  }
};

/**
 * Fetch all lessons (bulk fetch)
 * @param {string[]} lessonIds - Array of lesson IDs
 * @returns {Promise<Object>} Map of lessonId -> lesson data
 */
export const fetchMultipleLessons = async (lessonIds) => {
  try {
    const lessonsPromises = lessonIds.map(id => fetchLesson(id));
    const lessonsArray = await Promise.all(lessonsPromises);

    const lessonsMap = {};
    lessonIds.forEach((id, index) => {
      if (lessonsArray[index]) {
        lessonsMap[id] = lessonsArray[index];
      }
    });

    return lessonsMap;
  } catch (error) {
    console.error('Error fetching multiple lessons:', error);
    return {};
  }
};

/**
 * Build complete curriculum timeline from subject hierarchies
 * Combines hierarchy structure with subject/lesson metadata
 * Structure: Course → subjectIds array → Lessons via /testmeta/hierarchy/{subjectId}
 * @param {string[]} subjectIds - Array of subject IDs for the course
 * @returns {Promise<Array>} Timeline array with subjects and nested lessons
 */
export const buildCurriculumTimeline = async (subjectIds = []) => {
  try {
    console.log('[courseService] buildCurriculumTimeline called with subjectIds:', subjectIds);

    if (!subjectIds || subjectIds.length === 0) {
      throw new Error('No subject IDs provided for curriculum timeline');
    }

    // Fetch hierarchy for each subject in parallel
    // console.log('[courseService] Fetching hierarchies for subjects:', subjectIds);
    const hierarchyPromises = subjectIds.map(subjectId =>
      fetchSubjectHierarchy(subjectId)
    );
    const hierarchies = await Promise.all(hierarchyPromises);
    // console.log('[courseService] All hierarchies fetched:', hierarchies);

    // Build map of subjectId -> lessonIds
    const subjectLessonsMap = {};
    const allLessonIds = [];

    subjectIds.forEach((subjectId, index) => {
      const lessonIds = hierarchies[index];
      //console.log(`[courseService] Subject ${subjectId}: lessonIds =`, lessonIds, 'isArray?', Array.isArray(lessonIds), 'length?', lessonIds?.length);
      if (Array.isArray(lessonIds) && lessonIds.length > 0) {
        subjectLessonsMap[subjectId] = lessonIds;
        allLessonIds.push(...lessonIds);
      }
    });

    //console.log('[courseService] subjectLessonsMap:', subjectLessonsMap);
    //console.log('[courseService] allLessonIds:', allLessonIds);

    // Fetch subject metadata and lessons in parallel
    const [subjectMetadata, lessons] = await Promise.all([
      fetchMultipleSubjects(subjectIds),
      fetchMultipleLessons(allLessonIds)
    ]);

    // Build timeline structure using course's subjectIds order
    const timeline = subjectIds
      .map((subjectId, index) => {
        const lessonIds = subjectLessonsMap[subjectId];

        if (!Array.isArray(lessonIds) || lessonIds.length === 0) {
          return null;
        }

        const subjectData = subjectMetadata[subjectId];

        // Filter lessons to only include those with actual data
        const lessonsList = lessonIds
          .map((lessonId, lessonIndex) => {
            const lessonData = lessons[lessonId];

            // Only include lesson if it has actual data (name exists)
            if (!lessonData || !lessonData.name) {
              return null;
            }

            return {
              sequenceNumber: lessonIndex + 1,
              lessonId: lessonId,
              title: lessonData.name,
              description: lessonData.description || ''
            };
          })
          .filter(item => item !== null);

        // Only include subject if it has lessons with actual data
        if (lessonsList.length === 0) {
          return null;
        }

        return {
          sequenceNumber: index + 1,
          subjectId: subjectId,
          subjectTitle: subjectData?.name || `Subject ${subjectId}`,
          subjectDescription: subjectData?.description || '',
          lessons: lessonsList
        };
      })
      .filter(item => item !== null);

    return timeline;
  } catch (error) {
    console.error('Error building curriculum timeline:', error);
    throw error;
  }
};

// ============ COMBINED DATA ============

/**
 * Fetch complete course data (marketing + LMS + curriculum)
 * This is the main function used by CourseLanding.js
 * Fetches hierarchy from /testmeta/hierarchy/ (shared across all courses)
 * Uses subjectIds from course data to build curriculum timeline
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} Complete course data with all sections
 */
export const fetchCompleteCourseData = async (courseId) => {
  try {
    //console.log('[courseService] fetchCompleteCourseData called with courseId:', courseId);

    // Fetch marketing metadata and LMS course metadata first
    const [marketingData, lmsMetadata] = await Promise.all([
      fetchMarketingMetadata(courseId),
      fetchLMSCourseMetadata(courseId)
    ]);

    console.log('[courseService] LMS metadata:', lmsMetadata);
    console.log('[courseService] LMS metadata keys:', lmsMetadata ? Object.keys(lmsMetadata) : 'null');

    // Extract subject IDs from LMS course data
    const subjectIds = lmsMetadata?.subjectIds || [];
    console.log('[courseService] Extracted subjectIds:', subjectIds);

    // Fetch curriculum timeline with subject IDs
    let curriculumTimeline = null;
    if (subjectIds.length > 0) {
      // console.log('[courseService] Building curriculum timeline with subjectIds:', subjectIds);
      curriculumTimeline = await buildCurriculumTimeline(subjectIds);
      // console.log('[courseService] Curriculum timeline built:', curriculumTimeline);
    } else {
      console.warn('[courseService] No subjectIds found, skipping curriculum timeline');
    }

    // Combine all data (marketing takes precedence, LMS as fallback)
    const completeData = {
      ...marketingData,
      lmsMetadata: lmsMetadata,
      curriculum: {
        ...marketingData.curriculum,
        timeline: curriculumTimeline
      }
    };

    // console.log('[courseService] Complete data returned - curriculum timeline:', completeData.curriculum?.timeline);
    return completeData;
  } catch (error) {
    console.error('[courseService] Error fetching complete course data:', error);
    throw error;
  }
};
